/**
 * Minimal, dependency-free RFC 4180 CSV parser for the bulk supply import feature
 * (SeatBundleFloorsPage.jsx) -- Workstream 3.
 *
 * WHY HAND-WRITTEN INSTEAD OF A LIBRARY: this parses a spreadsheet a Cuedart-style supply partner
 * hands Kunal directly -- an untrusted third-party file, uploaded into a production admin tool.
 * The obvious library choice (SheetJS/`xlsx`, which also reads legacy .xls) currently ships two
 * open, unpatched high-severity advisories on the npm-published build (prototype pollution
 * GHSA-4r6h-8v6p-xvw6, ReDoS GHSA-5pgg-2g8v-p4x9 -- both "no fix available" on the npm registry;
 * SheetJS's own fixed builds are distributed only from their own CDN, which this project's network
 * egress allowlist doesn't reach). Adding a known-vulnerable, unpatched dependency specifically to
 * parse untrusted third-party files is exactly the kind of shortcut the project's own "handle real
 * money, real partners" standard rules out -- so this only accepts CSV, not the legacy binary .xls/
 * .xlsx formats (which have real internal complexity -- shared-string tables, formulas, styles --
 * that's genuinely hard to parse safely by hand). CSV has no macro/formula/external-reference
 * surface at all, and its grammar (RFC 4180) is small enough to implement correctly and legibly in
 * well under 100 lines -- lower real risk than a large, currently-unpatched third-party parser.
 * Kunal exports Cuedart's feed to CSV once per refresh (a 10-second Excel/Numbers/Sheets step)
 * before uploading here; see SeatBundleFloorsPage.jsx's upload copy for the operator-facing note.
 *
 * @param {string} text raw file text (already decoded; a leading UTF-8 BOM, common from
 *   Excel/Numbers CSV exports, is stripped automatically)
 * @returns {Array<Record<string,string>>} one plain object per data row, keyed by the header row's
 *   own column names verbatim (bundleSupplyImport.js's normalizeAndValidateRows on the server does
 *   its own case/alias-insensitive header matching, so this stays a dumb, faithful transcription)
 */
export function parseCsv(text) {
  const clean = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  const rows = parseRows(clean);
  if (!rows.length) return [];
  const header = rows[0];
  return rows.slice(1)
    .filter((row) => row.length > 1 || (row.length === 1 && row[0] !== ''))
    .map((row) => {
      const obj = {};
      header.forEach((h, i) => { obj[h] = row[i] ?? ''; });
      return obj;
    });
}

/**
 * @param {string} text
 * @returns {Array<Array<string>>}
 */
function parseRows(text) {
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;
  let i = 0;
  const n = text.length;

  while (i < n) {
    const ch = text[i];

    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') { field += '"'; i += 2; continue; } // escaped quote
        inQuotes = false;
        i++;
        continue;
      }
      field += ch;
      i++;
      continue;
    }

    if (ch === '"') { inQuotes = true; i++; continue; }
    if (ch === ',') { row.push(field); field = ''; i++; continue; }
    if (ch === '\r') { i++; continue; } // swallow bare CR; \r\n and \n both handled by the \n case below
    if (ch === '\n') {
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
      i++;
      continue;
    }
    field += ch;
    i++;
  }
  // Final field/row (files don't always end with a trailing newline).
  if (field !== '' || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  return rows;
}
